import React, { useState } from 'react';
import { Invoice } from '@/types/models';
import { formatCurrency } from '@/lib/format';
import { exportInvoicesToCSV } from '@/utils/exportUtils';
import { FilterControls } from './components/FilterControls';
import { GroupedInvoices } from './components/GroupedInvoices';
import { FlatInvoices } from './components/FlatInvoices';

interface InvoiceListProps {
  invoices: Invoice[];
  families: { id: string; name: string; }[];
  onAddInvoice: () => void;
  onEditInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (invoice: Invoice) => void;
  initialStatuses?: string[];
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  families,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  initialStatuses
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialStatuses || ['draft', 'unpaid', 'partial', 'paid', 'overdue']);
  const [groupByFamily, setGroupByFamily] = useState(true);

  const filteredInvoices = invoices.filter(invoice => {
    const familyName = invoice.family?.name || '';
    const matchesSearch = 
      familyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatuses.includes(invoice.status);
    // Only show partial invoices with amount_due > 0
    if (invoice.status === 'partial' && typeof invoice.amount_due === 'number') {
      return matchesSearch && matchesStatus && invoice.amount_due > 0;
    }
    return matchesSearch && matchesStatus;
  });

  const groupedInvoices = React.useMemo(() => {
    if (!groupByFamily) return { ungrouped: filteredInvoices };
    return filteredInvoices.reduce<Record<string, Invoice[]>>((acc, invoice) => {
      const familyId = invoice.family_id;
      if (!acc[familyId]) acc[familyId] = [];
      acc[familyId].push(invoice);
      return acc;
    }, {});
  }, [filteredInvoices, groupByFamily]);

  const familyMap = families.reduce((acc, family) => {
    acc[family.id] = family.name;
    return acc;
  }, {} as Record<string, string>);

  const formatAmount = (amountInCents: number): string => {
    return formatCurrency(amountInCents / 100);
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    setSelectedStatuses(prev => 
      checked 
        ? [...prev, status]
        : prev.filter(s => s !== status)
    );
  };

  const handleExportCSV = (): boolean => {
    try {
      return exportInvoicesToCSV(filteredInvoices, familyMap);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <FilterControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedStatuses={selectedStatuses}
        onStatusChange={handleStatusChange}
        onGroupToggle={() => setGroupByFamily(!groupByFamily)}
        groupByFamily={groupByFamily}
        onAddInvoice={onAddInvoice}
        onExportCSV={handleExportCSV}
        filteredInvoices={filteredInvoices}
      />

      {groupByFamily ? (
        <GroupedInvoices
          groupedInvoices={groupedInvoices}
          familyMap={familyMap}
          onEditInvoice={onEditInvoice}
          onDeleteInvoice={onDeleteInvoice}
          formatAmount={formatAmount}
        />
      ) : (
        <FlatInvoices
          invoices={filteredInvoices}
          onEditInvoice={onEditInvoice}
          onDeleteInvoice={onDeleteInvoice}
          formatAmount={formatAmount}
        />
      )}
    </div>
  );
};

export default InvoiceList;
